import { useState, useEffect } from "react";
import { collection, query, where, getDocs, updateDoc, doc } from "firebase/firestore";
import { db } from "../firebase";

/**
 * Busca el documento de paciente vinculado al usuario autenticado.
 * - Primero busca por email en la colección `patients`
 * - Si lo encuentra y no tiene linkedUserId, auto-vincula el UID del usuario
 * Retorna: { patient: object|null, loading: boolean, patientId: string|null }
 */
export default function usePatientProfile(user) {
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.email || user?.role !== "patient") {
      setLoading(false);
      setPatient(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        const snap = await getDocs(
          query(
            collection(db, "patients"),
            where("email", "==", user.email),
            where("activo", "==", true)
          )
        );

        if (cancelled || snap.empty) {
          if (!cancelled) setPatient(null);
          return;
        }

        const patDoc = snap.docs[0];
        const patData = { id: patDoc.id, ...patDoc.data() };

        // Auto-vincular si no tiene linkedUserId aún
        if (!patData.linkedUserId && user.uid) {
          try {
            await updateDoc(doc(db, "patients", patDoc.id), { linkedUserId: user.uid });
            patData.linkedUserId = user.uid;
          } catch (e) {
            // Falla silenciosa — posiblemente ya lo está actualizando el médico
          }
        }

        if (!cancelled) setPatient(patData);
      } catch (err) {
        if (!cancelled) {
          console.error("usePatientProfile:", err.message);
          setPatient(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [user?.email, user?.role, user?.uid]);

  return { patient, loading, patientId: patient?.id || null };
}
